import { API_BASE } from "./utils";

const KEY_PREFIX = "aivoryems.ticketEngine.";
const CACHE = new Map();
// const API_BASE = typeof window !== "undefined" ? "" : "http://localhost:8001";

function defaultTickets() {
  return [];
}

function defaultState() {
  return {
    eventId: null,
    tickets: defaultTickets(),
    promoCodes: [],
    purchases: [],
  };
}

function normalizeNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function normalizeState(eventId, raw) {
  const base = raw && typeof raw === "object" ? raw : {};
  const tickets = Array.isArray(base.tickets) ? base.tickets : [];
  const promoCodes = Array.isArray(base.promoCodes) ? base.promoCodes : [];
  const purchases = Array.isArray(base.purchases) ? base.purchases : [];
  return {
    eventId,
    tickets: tickets.map((t, idx) => ({
      id: t?.id || `ticket_${idx}`,
      type: t?.type || "ITEM",
      name: t?.name || `Ticket ${idx + 1}`,
      price: Math.max(0, normalizeNumber(t?.price, 0)),
      sold: Math.max(0, normalizeNumber(t?.sold, 0)),
      quota: Math.max(0, normalizeNumber(t?.quota, 0)),
      status: t?.status || "Selling",
      purchaseLimitPerEmail:
        t?.purchaseLimitPerEmail === null || t?.purchaseLimitPerEmail === undefined
          ? null
          : Math.max(1, normalizeNumber(t?.purchaseLimitPerEmail, 1)),
      startsAt: t?.startsAt || null,
      endsAt: t?.endsAt || null,
      allowCrossSell: t?.allowCrossSell !== false,
      trackCode: t?.trackCode || null,
    })),
    promoCodes: promoCodes.map((p, idx) => ({
      id: p?.id || `promo_${idx}`,
      code: String(p?.code || "").trim().toUpperCase(),
      discountPercent: Math.max(1, Math.min(100, normalizeNumber(p?.discountPercent, 10))),
      used: Math.max(0, normalizeNumber(p?.used, 0)),
      limit: p?.limit === null || p?.limit === undefined ? null : Math.max(1, normalizeNumber(p?.limit, 1)),
      startsAt: p?.startsAt || null,
      endsAt: p?.endsAt || null,
    })),
    purchases,
  };
}

function storageKey(eventId) {
  return `${KEY_PREFIX}${eventId}`;
}

export function loadTicketEngine(eventId) {
  if (CACHE.has(eventId)) return CACHE.get(eventId);
  const boot = window.__AIVORY_BOOTSTRAP__?.ticketEngines?.[eventId];
  if (boot) {
    const normalized = normalizeState(eventId, boot);
    CACHE.set(eventId, normalized);
    return normalized;
  }
  const raw = localStorage.getItem(storageKey(eventId));
  if (!raw) return normalizeState(eventId, defaultState());
  try {
    const parsed = JSON.parse(raw);
    const normalized = normalizeState(eventId, parsed);
    CACHE.set(eventId, normalized);
    return normalized;
  } catch {
    return normalizeState(eventId, defaultState());
  }
}

export function saveTicketEngine(eventId, state) {
  const normalized = normalizeState(eventId, state);
  CACHE.set(eventId, normalized);
  localStorage.setItem(storageKey(eventId), JSON.stringify(normalized));
  return fetch(`${API_BASE}/api/ticket-engine/${eventId}`, {
    method: "PUT",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ state: normalized }),
  }).then(async (res) => {
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || `Ticket engine save failed (${res.status})`);
    }
    return res.json().catch(() => ({ ok: true }));
  });
}

export async function syncTicketEngine(eventId) {
  const res = await fetch(`${API_BASE}/api/ticket-engine/${eventId}`);
  if (!res.ok) {
    throw new Error(`Ticket engine sync failed (${res.status})`);
  }
  const payload = await res.json();
  if (!payload?.state) {
    throw new Error("Ticket engine payload missing state");
  }
  const normalized = normalizeState(eventId, payload.state);
  CACHE.set(eventId, normalized);
  localStorage.setItem(storageKey(eventId), JSON.stringify(normalized));
  return normalized;
}

export function canEmailBuyTrack(state, email, incomingTrackId) {
  const normalizedEmail = email.trim().toLowerCase();
  const incoming = state.tickets.find((t) => t.id === incomingTrackId && t.type === "TRACK");
  if (!incoming) return true;
  if (incoming.allowCrossSell) return true;
  const priorDifferentTrack = state.purchases.some(
    (p) => p.email === normalizedEmail && p.type === "TRACK" && p.ticketId !== incomingTrackId,
  );
  return !priorDifferentTrack;
}

export function canPurchaseTicket(state, email, ticketId) {
  const normalizedEmail = email.trim().toLowerCase();
  const ticket = state.tickets.find((t) => t.id === ticketId);
  if (!ticket) return { ok: false, reason: "Ticket not found." };

  const now = Date.now();
  if (ticket.startsAt && now < new Date(ticket.startsAt).getTime()) {
    return { ok: false, reason: "This ticket is not on sale yet." };
  }
  if (ticket.endsAt && now > new Date(ticket.endsAt).getTime()) {
    return { ok: false, reason: "This ticket has expired." };
  }
  if (Number(ticket.sold || 0) >= Number(ticket.quota || 0)) {
    return { ok: false, reason: "This ticket is sold out." };
  }

  const perEmailPurchases = state.purchases.filter((p) => p.email === normalizedEmail && p.ticketId === ticketId).length;
  if (ticket.purchaseLimitPerEmail && perEmailPurchases >= ticket.purchaseLimitPerEmail) {
    return { ok: false, reason: "Purchase limit reached for this email." };
  }

  if (ticket.type === "TRACK" && !canEmailBuyTrack(state, normalizedEmail, ticket.id)) {
    return { ok: false, reason: "Track policy blocks this email from buying another track." };
  }

  if (ticket.type === "TIER") {
    const priorTier = state.purchases.some((p) => p.email === normalizedEmail && p.type === "TIER");
    if (priorTier) return { ok: false, reason: "This email already has a tier purchase." };
  }

  return { ok: true };
}

export function recordPurchase(state, email, purchasedTicketIds) {
  const normalizedEmail = email.trim().toLowerCase();
  const now = new Date().toISOString();
  const purchases = [...state.purchases];
  const tickets = state.tickets.map((ticket) => {
    if (!purchasedTicketIds.includes(ticket.id)) return ticket;
    const unitPrice = Number(ticket.price || 0);
    purchases.push({
      email: normalizedEmail,
      ticketId: ticket.id,
      type: ticket.type,
      unitPrice,
      lineTotal: unitPrice,
      at: now,
    });
    return { ...ticket, sold: Number(ticket.sold || 0) + 1 };
  });
  return { ...state, tickets, purchases };
}

export function validatePromoCode(state, code) {
  const normalized = code.trim().toUpperCase();
  if (!normalized) return { ok: false, reason: "Enter a promo code." };
  const promo = state.promoCodes.find((p) => p.code === normalized);
  if (!promo) return { ok: false, reason: "Promo code not found." };
  const now = Date.now();
  if (promo.startsAt && now < new Date(promo.startsAt).getTime()) {
    return { ok: false, reason: "Promo code is not active yet." };
  }
  if (promo.endsAt && now > new Date(promo.endsAt).getTime()) {
    return { ok: false, reason: "Promo code has expired." };
  }
  if (promo.limit !== null && promo.limit !== undefined && Number(promo.used || 0) >= Number(promo.limit)) {
    return { ok: false, reason: "Promo code redemption limit reached." };
  }
  return { ok: true, promo };
}

export function calculatePromoDiscount(subtotal, promo) {
  const amount = Number(subtotal || 0);
  if (!promo || amount <= 0) return 0;
  const pct = Math.max(0, Math.min(100, Number(promo.discountPercent || 0)));
  return (amount * pct) / 100;
}
